// Enhanced MUI Theme using "Natural Intelligence" Brand Identity
import { createTheme, ThemeOptions } from "@mui/material/styles";
import { DESIGN_TOKENS, TYPOGRAPHY_TOKENS, BREAKPOINTS } from "../tokens";

// Create the agrotech-focused theme
export const muiTheme = createTheme({
  palette: {
    primary: {
      main: DESIGN_TOKENS.colors.brand.green, // Agricultural Green
      light: DESIGN_TOKENS.colors.brand.sage, // Smart Sage
      dark: DESIGN_TOKENS.colors.brand.forest, // Forest Green
      contrastText: DESIGN_TOKENS.colors.text.inverse,
    },
    secondary: {
      main: DESIGN_TOKENS.colors.brand.stone, // Professional Stone
      light: DESIGN_TOKENS.colors.secondary[300],
      dark: DESIGN_TOKENS.colors.secondary[700],
      contrastText: DESIGN_TOKENS.colors.text.inverse,
    },
    success: {
      main: DESIGN_TOKENS.colors.brand.primary, // Azul do tema
      light: DESIGN_TOKENS.colors.brand.surface,
      dark: DESIGN_TOKENS.colors.brand.primaryVariant,
      contrastText: DESIGN_TOKENS.colors.text.inverse,
    },
    warning: {
      main: DESIGN_TOKENS.colors.status.warning[500],
      light: DESIGN_TOKENS.colors.status.warning[50],
      dark: DESIGN_TOKENS.colors.status.warning[600],
    },
    error: {
      main: DESIGN_TOKENS.colors.status.error[500],
      light: DESIGN_TOKENS.colors.status.error[50],
      dark: DESIGN_TOKENS.colors.status.error[600],
    },
    info: {
      main: DESIGN_TOKENS.colors.status.info[500],
      light: DESIGN_TOKENS.colors.status.info[50],
      dark: DESIGN_TOKENS.colors.status.info[600],
    },
    background: {
      default: DESIGN_TOKENS.colors.background.default,
      paper: DESIGN_TOKENS.colors.background.paper,
    },
    text: {
      primary: DESIGN_TOKENS.colors.text.primary,
      secondary: DESIGN_TOKENS.colors.text.secondary,
      disabled: DESIGN_TOKENS.colors.text.disabled,
    },
    grey: {
      50: DESIGN_TOKENS.colors.neutral[50],
      100: DESIGN_TOKENS.colors.neutral[100],
      200: DESIGN_TOKENS.colors.neutral[200],
      300: DESIGN_TOKENS.colors.neutral[300],
      400: DESIGN_TOKENS.colors.neutral[400],
      500: DESIGN_TOKENS.colors.neutral[500],
      600: DESIGN_TOKENS.colors.neutral[600],
      700: DESIGN_TOKENS.colors.neutral[700],
      800: DESIGN_TOKENS.colors.neutral[800],
      900: DESIGN_TOKENS.colors.neutral[900],
    },
  },
  typography: {
    fontFamily: TYPOGRAPHY_TOKENS.fonts.primary,
    // Headings using Poppins for friendly, approachable feel
    h1: {
      ...TYPOGRAPHY_TOKENS.textStyles.h1,
      color: DESIGN_TOKENS.colors.text.primary,
      [`@media (max-width:${BREAKPOINTS.tablet}px)`]: {
        fontSize: TYPOGRAPHY_TOKENS.responsive.tablet.h1.fontSize,
        lineHeight: TYPOGRAPHY_TOKENS.responsive.tablet.h1.lineHeight,
      },
      [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
        fontSize: TYPOGRAPHY_TOKENS.responsive.mobile.h1.fontSize,
        lineHeight: TYPOGRAPHY_TOKENS.responsive.mobile.h1.lineHeight,
      },
    },
    h2: {
      ...TYPOGRAPHY_TOKENS.textStyles.h2,
      color: DESIGN_TOKENS.colors.text.primary,
      [`@media (max-width:${BREAKPOINTS.tablet}px)`]: {
        fontSize: TYPOGRAPHY_TOKENS.responsive.tablet.h2.fontSize,
      },
      [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
        fontSize: TYPOGRAPHY_TOKENS.responsive.mobile.h2.fontSize,
        lineHeight: TYPOGRAPHY_TOKENS.responsive.mobile.h2.lineHeight,
      },
    },
    h3: {
      ...TYPOGRAPHY_TOKENS.textStyles.h3,
      color: DESIGN_TOKENS.colors.text.primary,
      [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
        fontSize: TYPOGRAPHY_TOKENS.responsive.mobile.h3.fontSize,
      },
    },
    h4: {
      ...TYPOGRAPHY_TOKENS.textStyles.h4,
      color: DESIGN_TOKENS.colors.text.primary,
    },
    h5: {
      ...TYPOGRAPHY_TOKENS.textStyles.h5,
      color: DESIGN_TOKENS.colors.text.primary,
    },
    h6: {
      ...TYPOGRAPHY_TOKENS.textStyles.h6,
      color: DESIGN_TOKENS.colors.text.primary,
    },
    // Body text using Inter for optimal readability
    body1: {
      ...TYPOGRAPHY_TOKENS.textStyles.body1,
      color: DESIGN_TOKENS.colors.text.secondary,
    },
    body2: {
      ...TYPOGRAPHY_TOKENS.textStyles.body2,
      color: DESIGN_TOKENS.colors.text.tertiary,
    },
    button: {
      ...TYPOGRAPHY_TOKENS.textStyles.button,
    },
    caption: {
      ...TYPOGRAPHY_TOKENS.textStyles.caption,
      color: DESIGN_TOKENS.colors.text.tertiary,
    },
    overline: {
      ...TYPOGRAPHY_TOKENS.textStyles.overline,
      color: DESIGN_TOKENS.colors.text.tertiary,
    },
  },
  shape: {
    borderRadius: 10, // Fazenda style - keeping existing value
  },
  spacing: 8, // MUI default 8px base unit
  breakpoints: {
    values: {
      xs: 0,
      sm: BREAKPOINTS.mobile,
      md: BREAKPOINTS.tablet,
      lg: BREAKPOINTS.desktop,
      xl: BREAKPOINTS.large,
    },
  },
  components: {
    // Button component overrides
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: "none",
          fontSize: TYPOGRAPHY_TOKENS.fontSizes.sm,
          fontWeight: TYPOGRAPHY_TOKENS.fontWeights.semibold,
          padding: "12px 24px",
          boxShadow: "none",
          minHeight: "44px", // Accessibility
          transition: "all 0.2s ease",
          fontFamily: TYPOGRAPHY_TOKENS.fonts.primary,
          "&:hover": {
            transform: "translateY(-1px)",
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.brand.green} 0%, ${DESIGN_TOKENS.colors.brand.sage} 100%)`,
          color: DESIGN_TOKENS.colors.text.inverse,
          "&:hover": {
            background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.brand.forest} 0%, ${DESIGN_TOKENS.colors.brand.green} 100%)`,
            boxShadow: `0 4px 12px rgba(45, 80, 22, 0.15)`,
          },
        },
        outlined: {
          borderColor: DESIGN_TOKENS.colors.neutral[200],
          color: DESIGN_TOKENS.colors.text.secondary,
          "&:hover": {
            borderColor: DESIGN_TOKENS.colors.brand.green,
            backgroundColor: `rgba(45, 80, 22, 0.04)`,
          },
        },
        // Stone accent variant
        containedSecondary: {
          background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.brand.stone} 0%, ${DESIGN_TOKENS.colors.secondary[600]} 100%)`,
          color: DESIGN_TOKENS.colors.text.inverse,
          "&:hover": {
            background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.brand.charcoal} 0%, ${DESIGN_TOKENS.colors.brand.stone} 100%)`,
            boxShadow: `0 4px 12px rgba(120, 113, 108, 0.25)`,
          },
        },
      },
    },
    // TextField component overrides
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: DESIGN_TOKENS.colors.background.paper,
            borderRadius: 10,
            fontSize: TYPOGRAPHY_TOKENS.fontSizes.base,
            fontFamily: TYPOGRAPHY_TOKENS.fonts.primary,
            "& fieldset": {
              borderColor: DESIGN_TOKENS.colors.neutral[200],
            },
            "&:hover fieldset": {
              borderColor: DESIGN_TOKENS.colors.brand.green,
            },
            "&.Mui-focused fieldset": {
              borderColor: DESIGN_TOKENS.colors.brand.green,
              borderWidth: "1px",
            },
          },
          "& .MuiInputLabel-root": {
            fontFamily: TYPOGRAPHY_TOKENS.fonts.primary,
            "&.Mui-focused": {
              color: DESIGN_TOKENS.colors.brand.green,
            },
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontFamily: TYPOGRAPHY_TOKENS.fonts.primary,
          color: DESIGN_TOKENS.colors.brand.green,
          "&:hover": {
            textDecoration: "underline",
            color: DESIGN_TOKENS.colors.brand.forest,
          },
        },
      },
    },
    // AppBar component overrides
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: `rgba(246, 249, 251, 0.95)`,
          backdropFilter: "blur(12px)",
          boxShadow: `0 1px 3px rgba(30, 41, 59, 0.1)`,
          borderBottom: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
        },
      },
    },
    // Card component overrides
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${DESIGN_TOKENS.colors.neutral[100]}`,
        },
      },
    },
    // Paper component overrides
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: DESIGN_TOKENS.colors.background.paper,
        },
        elevation1: {
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
        },
      },
    },
    // Chip component overrides
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: TYPOGRAPHY_TOKENS.fontWeights.medium,
        },
      },
    },
    // List component overrides
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: DESIGN_TOKENS.colors.background.paper,
        },
      },
    },
  },
} as ThemeOptions);
